import type { SixDigitCode } from '@/types/utils/sixDigitCode.ts';
import { isSpaceKeyEvent } from '@/utils/keyboard.ts';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { getStackClassName } from '@nautilusbio/nb-design-system/Stack';
import Text from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useCopyToClipboard } from '@nautilusbio/nb-design-system/useCopyToClipboard';
import { useTimeout } from '@nautilusbio/nb-design-system/useTimeout';
import clsx from 'clsx';
import { CopyIcon } from 'lucide-react';
import React, { type FC, type KeyboardEvent, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.css';

const COPY_FEEDBACK_DURATION_MS = 1200;

const Code: FC<{ code: SixDigitCode; id: string }> = ({ code, id }) => {
  const { t } = useTranslation('general');
  const { copy } = useCopyToClipboard();
  const { set: scheduleReset } = useTimeout();

  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [hovered, setHovered] = useState(false);

  const handleCopy = useCallback(() => {
    // DS copy() is void — errors are caught and logged internally
    copy(code);
    toast.success(`${t('copied')}"${code}"`);

    if (btnRef.current) {
      btnRef.current.dataset.copied = 'true';
      // blur on next tick to ensure any native handling completes
      setTimeout(() => {
        if (btnRef.current) btnRef.current.blur();
      }, 0);

      scheduleReset(() => {
        if (btnRef.current) btnRef.current.dataset.copied = 'false';
      }, COPY_FEEDBACK_DURATION_MS);
    }

    // ensure hover-based label state is reset
    setHovered(false);
  }, [code, copy, scheduleReset, t]);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    if (btnRef.current) btnRef.current.dataset.hover = 'true';
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    if (btnRef.current) btnRef.current.dataset.hover = 'false';
  }, []);

  const handleFocus = handleMouseEnter;
  const handleBlur = handleMouseLeave;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      // Accept multiple representations of the Space key across browsers
      if (event.key === 'Enter' || isSpaceKeyEvent(event)) {
        event.preventDefault();
        handleCopy();
      }
    },
    [handleCopy],
  );

  return (
    <Button
      aria-label={`Copy code ${code}`}
      className={clsx(styles.codeWrapper, styles.code, getStackClassName(['displayflex', 'horizontal', 'gap1']))}
      data-hover={hovered ? 'true' : 'false'}
      data-testid={`${id}-copy-btn`}
      id={id}
      ref={btnRef}
      onPress={() => handleCopy()}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      tabIndex={0}
      visual="unstyled"
    >
      <Text as="span" aria-hidden={false}>
        {hovered ? t('copy_question') : t('code')}
      </Text>

      <Text as="strong">{code}</Text>

      <CopyIcon className={styles.copyIcon} aria-hidden size={14} />
    </Button>
  );
};

Code.displayName = 'Code';

export default Code;
